import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { MultiStepFormStepChangeDirections } from 'models/app/multiStepsFormModel';
import { ToastType } from 'models/app/toast';
import { Epic } from 'models/meta/epic';

import { transactionsListRootRoutePath } from 'config/routes';
import { createTransactionDetailsPath } from 'config/routes.helpers';

import { showToast } from 'store/application/actions';
import { updateList } from 'store/list/actions';
import { hideModal } from 'store/modal/actions';
import { requestSetMultiStepsFormStep, setNewEntityLocation } from 'store/multi-steps-form/actions';
import { clearScaChallenge } from 'store/sca-challange/actions';
import { transactionsListsPerLocationStoreKeyName, transactionsReducerName } from 'store/transactions/reducer';

import { AddBankTransferFormSteps } from 'components/pages/authorised/payments/AddBankTransferPage/AddBankTransferPage.types';
import { InternalTransferFormSteps } from 'components/pages/authorised/payments/InternalTransferPage/InternalTransferPage.types';

import { CREATE_PAYMENT, DELETE_TRANSACTION_ATTACHMENT, FETCH_TRANSACTION_DETAILS, UPDATE_RECEIPT_TITLE, UPLOAD_RECEIPT_FILE } from './action.types';
import {
    createPaymentSuccess,
    createPaymentFailure,
    fetchTransactionDetailsFailure,
    fetchTransactionDetailsSuccess,
    uploadReceiptFileSuccess,
    fetchTransactionDetails,
    uploadReceiptFileFailure,
    updateReceiptTitleSuccess,
    updateReceiptTitleFailure,
    deleteTransactionAttachmentFailure,
} from './actions';
import { createCreatePaymentErrorMessage } from './epics.helpers';
import { transactionDetailsPerLocationStoreKeyName, transactionReducerName } from './reducer';


const getCurrentQueryParams = (state, pathname: string) => {
    return pathname === transactionsListRootRoutePath
        ? state[transactionsReducerName]?.[
            transactionsListsPerLocationStoreKeyName
        ]?.[pathname]?.queryParams
        : state[transactionReducerName]?.[
            transactionDetailsPerLocationStoreKeyName
        ]?.[pathname]?.queryParams;
};


export const onFetchTransactionDetails: Epic = (action$, state$, { transactions }) => action$.pipe(
    ofType(FETCH_TRANSACTION_DETAILS),
    switchMap(({ payload }) => from(transactions.getTransactionDetails(payload.queryParams.transactionUuid))
        .pipe(
            switchMap((response) => of(fetchTransactionDetailsSuccess({
                responsePayload: response.data,
                locationPathname: payload.locationPathname,
            }))),
            catchError(() => of(fetchTransactionDetailsFailure(payload.locationPathname))),
        )),
);
export const onCreateP2PTransaction: Epic = (action$, state$, {
    transactions,
    i18n,
}) => action$.pipe(
    ofType(CREATE_PAYMENT),
    switchMap(({ payload }) => from(transactions.createNewPayment(payload))
        .pipe(
            switchMap((response) => {
                const isBankTransfer = state$.value?.multiStepsForm?.currentStep === AddBankTransferFormSteps.REVIEW;
                const currentStep = isBankTransfer ? AddBankTransferFormSteps.REVIEW : InternalTransferFormSteps.REVIEW;

                return of(
                    clearScaChallenge(),
                    createPaymentSuccess(response.data),
                    setNewEntityLocation(createTransactionDetailsPath(response?.data?.transactionUuid)),
                    requestSetMultiStepsFormStep(currentStep, MultiStepFormStepChangeDirections.FORWARD),
                    updateList({
                        findByPropName: 'transactionUuid',
                        findByPropValue: response?.data?.transactionUuid,
                        listReducerName: transactionsReducerName,
                    }),
                );
            }),
            catchError((error) => of(
                showToast({
                    type: ToastType.error,
                    message: createCreatePaymentErrorMessage(i18n.t, error),
                }),
                clearScaChallenge(),
                createPaymentFailure(),
            )),
        )),
);


export const onUploadReceiptFile = (action$, state$, { transactions, i18n }) =>
    action$.pipe(
        ofType(UPLOAD_RECEIPT_FILE),
        switchMap(({ payload }) =>
            from(transactions.uploadReceiptFile(payload)).pipe(
                switchMap(() => {
                    const pathname = payload.locationPathname;
                    const currentQueryParams = getCurrentQueryParams(
                        state$.value,
                        pathname,
                    );

                    const commonActions = [
                        uploadReceiptFileSuccess(),
                        hideModal(),
                        updateList({
                            findByPropName: 'id',
                            findByPropValue: payload.queryParams.transactionId,
                            listReducerName: transactionReducerName,
                        }),
                        showToast({
                            type: ToastType.success,
                            message: i18n.t('common:actionMsg.genericSuccessMessage.text'),
                        }),
                        fetchTransactionDetails({
                            queryParams: currentQueryParams,
                            locationPathname: pathname,
                        }),
                    ];

                    return of(
                        ...commonActions,
                        ...(pathname !== transactionsListRootRoutePath
                            ? [
                                fetchTransactionDetails({
                                    queryParams: currentQueryParams,
                                    locationPathname: pathname,
                                }),
                            ]
                            : []),
                    );
                }),
                catchError((error) =>
                    of(
                        uploadReceiptFileFailure(error),
                        showToast({
                            type: ToastType.error,
                            message: error.message,
                        }),
                    ),
                ),
            ),
        ),
    );

export const onUpdateReceiptTitle: Epic = (action$, state$, { transactions, i18n }) =>
    action$.pipe(
        ofType(UPDATE_RECEIPT_TITLE),
        switchMap(({ payload }) => {
            return from(transactions.updateReceiptTitle(payload)).pipe(
                switchMap(() => {
                    const pathname = payload.locationPathname;
                    const currentQueryParams = getCurrentQueryParams(
                        state$.value,
                        pathname,
                    );

                    const commonActions = [
                        updateReceiptTitleSuccess(),
                        hideModal(),
                        updateList({
                            findByPropName: 'id',
                            findByPropValue: payload.queryParams.transactionId,
                            listReducerName: transactionReducerName,
                        }),
                        showToast({
                            type: ToastType.success,
                            message: i18n.t('common:actionMsg.genericSuccessMessage.text'),
                        }),
                        fetchTransactionDetails({
                            queryParams: currentQueryParams,
                            locationPathname: pathname,
                        }),
                    ];

                    return of(
                        ...commonActions,
                        ...(pathname !== transactionsListRootRoutePath
                            ? [
                                fetchTransactionDetails({
                                    queryParams: currentQueryParams,
                                    locationPathname: pathname,
                                }),
                            ]
                            : []),
                    );
                }),
                catchError(() =>
                    of(
                        updateReceiptTitleFailure(),
                        showToast({
                            type: ToastType.error,
                            message: i18n.t('common:actionMsg.genericErrorMessage.text'),
                        }),
                    ),
                ),
            );
        }),
    );

export const onDeleteTransactionAttachment: Epic = (action$, state$, { transactions, i18n }) =>
    action$.pipe(
        ofType(DELETE_TRANSACTION_ATTACHMENT),
        switchMap(({ payload }) => {
            return from(
                transactions.deleteTransactionReceiptById(
                    payload.transactionId,
                    payload.attachmentId,
                ),
            ).pipe(
                switchMap(() => {
                    const pathname = payload.locationPathname;
                    const currentQueryParams = getCurrentQueryParams(
                        state$.value,
                        pathname,
                    );

                    const commonActions = [
                        showToast({
                            type: ToastType.success,
                            message: i18n.t('common:actionMsg.genericSuccessMessage.text'),
                        }),
                    ];

                    return of(
                        ...commonActions,
                        ...(pathname !== transactionsListRootRoutePath
                            ? [
                                fetchTransactionDetails({
                                    queryParams: currentQueryParams,
                                    locationPathname: pathname,
                                }),
                            ]
                            : []),
                    );
                }),
                catchError(() =>
                    of(
                        deleteTransactionAttachmentFailure(),
                        showToast({
                            type: ToastType.error,
                            message: i18n.t('common:actionMsg.genericErrorMessage.text'),
                        }),
                    ),
                ),
            );
        }),
    );


export default [
    onFetchTransactionDetails,
    onCreateP2PTransaction,
    onUploadReceiptFile,
    onUpdateReceiptTitle,
    onDeleteTransactionAttachment,
];
